<script setup>
import { computed, defineEmits, defineProps, toRefs } from "vue";

import { parseAndFormatDateStringToDate, parseAndFormatISODateStringToDate } from "../../../util/date";
import { formattedPriceFromCentsWithoutCents } from "../../../util/formatter";

import PriceIcon from "../../shared/icons/PriceIcon.vue";
import SimplePopup from "@/components/shared/SimplePopup.vue";
import SkeletonLoader from "@/components/shared/loaders/BaseSkeletonLoader.vue";
import { CalendarHourIcon, CalendarIcon } from "../../shared/icons";

const emit = defineEmits(["closeModal"]);

const props = defineProps({
  session: {
    type: Object,
    required: true,
    default: () => {},
  },
  price: {
    type: Object,
    required: true,
    default: () => {},
  },
  isLoadingPriceBreakdown: {
    type: Boolean,
    default: true,
  },
  openModal: {
    type: Boolean,
    default: false,
  },
});

const { session, price, isLoadingPriceBreakdown, openModal } = toRefs(props);

const programDates = computed(() => {
  return `${
    price.value?.session_start_date
      ? parseAndFormatDateStringToDate(price.value?.session_start_date, "yyyy-MM-dd")
      : ""
  } - ${
    price.value?.session_end_date ? parseAndFormatDateStringToDate(price.value?.session_end_date, "yyyy-MM-dd") : ""
  }`;
});

const sessionApplicationDeadline = computed(() =>
  price.value?.session_application_deadline
    ? parseAndFormatISODateStringToDate(price.value?.session_application_deadline, "MMM dd, yyyy")
    : ""
);

const housingUpgradePrice = computed(
  () => price.value?.rooms?.reduce((acc, cur) => acc + (cur?.price_difference_from_default ?? 0), 0) ?? 0
);

const startingPriceBreakdown = computed(() =>
  [
    {
      name: "Educational Fee",
      price:
        session.value?.order?.basePrice?.total_academic_fees_price_in_cents ??
        price.value?.session_base_educational_fee ??
        0,
    },
    {
      name: "Housing Fee",
      price: session.value?.order?.basePrice?.housing_price_in_cents ?? price.value?.session_base_housing_fee ?? 0,
    },
    {
      name: "Medical Insurance",
      price: session.value?.order?.basePrice?.insurance_price_in_cents ?? price.value?.session_base_insurance_fee ?? 0,
    },
    {
      name: "Transportation Fee",
      price:
        session.value?.order?.basePrice?.public_transportation_price_in_cents ??
        price.value?.session_base_transportation_fee ??
        0,
    },
    {
      name: "Group Flight Fee",
      price:
        session.value?.order?.basePrice?.group_flight_price_in_cents ??
        price.value?.session_base_group_flight_fee ??
        0,
    },
  ]
    .filter((item) => item?.price > 0)
    .sort((obj11, obj2) => (obj11?.price ?? 0) - (obj2?.price ?? 0))
);

const studentAddonsPriceBreakdown = computed(() =>
  [
    ...(price.value.addons?.map((item) => ({
      name: item?.product_name,
      price: item?.student_total_price_in_cents ?? 0,
    })) ?? []),
    ...(price.value.event_occurrences
      ?.filter((item) => item?.price_difference_from_default > 0)
      ?.map((item) => ({
        name: item?.product_name,
        price: item?.student_total_price_in_cents ?? 0,
      })) ?? []),
    { name: "Housing Upgrade", price: housingUpgradePrice.value },
  ]
    .filter((item) => item?.price > 0)
    .sort((obj11, obj2) => obj11?.price - obj2?.price)
);

const studentProgramTotal = computed(
  () =>
    (startingPriceBreakdown.value?.reduce((acc, cur) => acc + cur.price, 0) ?? 0) +
    (studentAddonsPriceBreakdown.value?.reduce((acc, cur) => acc + cur.price, 0) ?? 0)
);
</script>
<template>
  <SimplePopup
    custom-class="z-[51] font-montserrat"
    custom-content-class="p-6"
    data-testid="my-students-price-breakdown-modal"
    :open="openModal"
    :header-color-class="'bg-teal-accent'"
    :full-screen="false"
    :show-close-button="false"
    @close="emit('closeModal')"
  >
    <template #title>
      <div class="flex self-center text-indigo-base">
        <div class="self-center pl-2 uppercase font-bold text-sm">
          <div class="flex flex-1 flex-row"><PriceIcon /><span class="ml-2">Price Breakdown</span></div>
        </div>
      </div>
    </template>
    <template #content>
      <div>
        <div class="flex flex-col">
          <div class="flex flex-1 flex-col">
            <div class="flex flex-1 w-full text-2xl self-start font-bold text-left">
              {{ session.program }}
            </div>
            <div class="flex flex-row mt-2">
              <div class="flex flex-row items-center">
                <CalendarIcon class="mr-2" />
                <span class="mr-2 !font-normal text-xs md:text-sm">Program dates</span>
                <span class="text-xs md:text-sm">{{ programDates }}</span>
              </div>
            </div>
            <div class="flex flex-row mt-2">
              <div class="flex flex-row items-center">
                <CalendarHourIcon class="mr-2" />
                <span class="mr-2 !font-normal text-xs md:text-sm">Application deadline</span>
                <div>
                  <SkeletonLoader v-if="isLoadingPriceBreakdown" />
                  <span v-else class="text-xs md:text-sm">{{ sessionApplicationDeadline }}</span>
                </div>
              </div>
            </div>
            <div class="flex flex-row mt-2 text-cyan-200">
              <SkeletonLoader v-if="isLoadingPriceBreakdown" />
              <span v-else class="font-bold">
                STARTING AT
                {{ formattedPriceFromCentsWithoutCents(price?.session_base_price ?? 0) }}
              </span>
            </div>
          </div>

          <SkeletonLoader v-if="isLoadingPriceBreakdown" />
          <div v-else>
            <div v-if="startingPriceBreakdown?.length > 0" class="flex flex-1 flex-col mt-4">
              <div class="flex flex-1 flex-col self-start w-full">
                <span class="self-start text-lg">Starting Price Breakdown</span>
                <hr class="w-full mt-2" />
                <div class="flex flex-1 flex-col self-start w-full">
                  <div v-for="(item, idx) in startingPriceBreakdown" :key="item?.name || idx">
                    <div
                      v-if="item?.price"
                      class="flex flex-1 flex-row w-full px-4 py-2 tracking-wide"
                      :class="{ 'bg-gray-75': idx % 2 === 0 }"
                    >
                      <div class="flex flex-1 self-start text-left w-full">
                        {{ item.name }}
                      </div>
                      <span class="self-end">{{ formattedPriceFromCentsWithoutCents(item.price) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="studentAddonsPriceBreakdown?.length > 0" class="flex flex-1 flex-col mt-4">
              <div class="flex flex-1 flex-col self-start w-full">
                <span class="self-start text-lg">Student Add-ons Breakdown</span>
                <hr class="w-full mt-2" />
                <div class="flex flex-1 flex-col self-start w-full">
                  <div v-for="(item, idx) in studentAddonsPriceBreakdown" :key="item?.name || idx">
                    <div
                      v-if="item?.price"
                      class="flex flex-1 flex-row w-full px-4 py-2 tracking-wide"
                      :class="{ 'bg-gray-75': idx % 2 === 0 }"
                    >
                      <div class="flex flex-1 self-start text-left w-full">
                        {{ item.name }}
                      </div>
                      <span class="self-end">{{ formattedPriceFromCentsWithoutCents(item.price) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-1 flex-col mt-4">
              <div class="flex flex-1 flex-col">
                <div class="flex flex-1 flex-row">
                  <div class="flex flex-1 flex-col self-start w-full">
                    <span class="self-start text-left text-lg">Student Program Total</span>
                  </div>
                  <span class="mr-4">{{ formattedPriceFromCentsWithoutCents(studentProgramTotal ?? 0) }}</span>
                </div>
              </div>
              <hr class="w-full mt-2" />
            </div>
          </div>
        </div>
      </div>
    </template>
  </SimplePopup>
</template>
